/**
 * Переводы экранов ролевой модели: роли, матрица, реестр разделов, журнал
 * доступов (DEV-1268). Подмешиваются в базовые локали через deepMerge.
 *
 * Отдельный неймспейс `admin.rbac`: ветка `admin.roles` уже занята словарём
 * названий ролей по ключу (useRoleLabel), и складывать туда подписи экрана
 * значило бы однажды получить роль с ключом TITLE.
 */
type LocaleOverride = Record<string, unknown>

type Locale = 'ru' | 'en' | 'vn'

const build = (l: Locale): LocaleOverride => ({
  common: {
    navigation: {
      adminRoles: { ru: 'Роли и права', en: 'Roles', vn: 'Vai trò' }[l],
      adminSections: { ru: 'Реестр разделов', en: 'Sections', vn: 'Danh mục mục' }[l],
      adminAudit: { ru: 'Журнал доступов', en: 'Access log', vn: 'Nhật ký truy cập' }[l],
    },
  },
  admin: {
    rbac: {
      // Ключевая строка эпика: право уезжает в кэш валидации токена на 30
      // секунд. Без предупреждения администратор решит, что не сработало, и
      // нажмёт ещё раз — а потом ещё.
      cacheNotice: {
        ru: 'Изменения прав вступают в силу в течение 30 секунд: столько живёт кэш проверки токена.',
        en: 'Permission changes take effect within 30 seconds — that is how long the token validation cache lives.',
        vn: 'Thay đổi quyền có hiệu lực trong vòng 30 giây — đó là thời gian bộ đệm xác thực token.',
      }[l],
      roles: {
        title: { ru: 'Роли', en: 'Roles', vn: 'Vai trò' }[l],
        create: { ru: 'Создать роль', en: 'Create role', vn: 'Tạo vai trò' }[l],
        matrix: { ru: 'Матрица прав', en: 'Permission matrix', vn: 'Ma trận quyền' }[l],
        hint: {
          ru: 'Системные роли (со щитом) нельзя удалить и нельзя переименовать ключ — они заводятся миграцией. Роль с носителями не удаляется, пока назначена.',
          en: 'System roles (with a shield) cannot be deleted and their key cannot be renamed — they are seeded by a migration. A role with holders cannot be deleted while it is assigned.',
          vn: 'Vai trò hệ thống (có khiên) không thể xóa và không thể đổi khóa — chúng được tạo bằng migration. Vai trò đang được gán không thể xóa.',
        }[l],
        columns: {
          key: { ru: 'Ключ', en: 'Key', vn: 'Khóa' }[l],
          name: { ru: 'Название', en: 'Name', vn: 'Tên' }[l],
          description: { ru: 'Описание', en: 'Description', vn: 'Mô tả' }[l],
          apps: { ru: 'Приложения', en: 'Applications', vn: 'Ứng dụng' }[l],
          permissions: { ru: 'Прав', en: 'Perms', vn: 'Quyền' }[l],
          holders: { ru: 'Носителей', en: 'Holders', vn: 'Người giữ' }[l],
        },
        locks: {
          system: {
            ru: 'Системная роль: заводится и удаляется только миграцией',
            en: 'System role: seeded and removed only by a migration',
            vn: 'Vai trò hệ thống: chỉ tạo và xóa bằng migration',
          }[l],
          // Без числительного согласования: «назначена 1 пользователям» —
          // ровно та мелочь, по которой интерфейс читается как недоделанный,
          // а полноценная русская плюрализация ради тултипа не окупается.
          holders: {
            ru: 'У роли есть носители: {count}. Сначала снимите её со всех',
            en: 'The role has {count} holder(s). Remove it from all of them first',
            vn: 'Vai trò có {count} người giữ. Hãy gỡ khỏi tất cả trước',
          }[l],
          permissions: {
            ru: 'Права системной роли правятся только миграцией',
            en: 'Permissions of a system role are changed only by a migration',
            vn: 'Quyền của vai trò hệ thống chỉ thay đổi bằng migration',
          }[l],
        },
        form: {
          createTitle: { ru: 'Новая роль', en: 'New role', vn: 'Vai trò mới' }[l],
          editTitle: { ru: 'Правка роли', en: 'Edit role', vn: 'Sửa vai trò' }[l],
          key: { ru: 'Ключ роли', en: 'Role key', vn: 'Khóa vai trò' }[l],
          keyHint: {
            ru: 'Машинный идентификатор: латиница и подчёркивание, например SALES_LEAD. Изменить его потом нельзя',
            en: 'Machine identifier: latin letters and underscores, for example SALES_LEAD. It cannot be changed later',
            vn: 'Định danh máy: chữ latinh và gạch dưới, ví dụ SALES_LEAD. Sau này không thể đổi',
          }[l],
          keyLocked: {
            ru: 'Ключ неизменяем: по нему роль сверяют другие сервисы',
            en: 'The key is immutable: other services match the role by it',
            vn: 'Khóa không thể đổi: các dịch vụ khác đối chiếu vai trò theo khóa',
          }[l],
          keyRequired: {
            ru: 'Укажите ключ роли',
            en: 'Role key is required',
            vn: 'Cần nhập khóa vai trò',
          }[l],
          name: { ru: 'Название', en: 'Name', vn: 'Tên' }[l],
          description: { ru: 'Описание', en: 'Description', vn: 'Mô tả' }[l],
        },
        toast: {
          created: { ru: 'Роль создана', en: 'Role created', vn: 'Đã tạo vai trò' }[l],
          updated: { ru: 'Роль сохранена', en: 'Role saved', vn: 'Đã lưu vai trò' }[l],
          deleted: { ru: 'Роль удалена', en: 'Role deleted', vn: 'Đã xóa vai trò' }[l],
        },
        empty: {
          title: { ru: 'Ролей нет', en: 'No roles', vn: 'Không có vai trò' }[l],
          description: {
            ru: 'Справочник ролей пуст — вероятно, справочник ещё не посеян миграцией',
            en: 'The role directory is empty — it has probably not been seeded by a migration yet',
            vn: 'Danh mục vai trò trống — có lẽ chưa được tạo bằng migration',
          }[l],
        },
      },
      matrix: {
        title: {
          ru: 'Матрица роль × право',
          en: 'Role × permission matrix',
          vn: 'Ma trận vai trò × quyền',
        }[l],
        permission: { ru: 'Право', en: 'Permission', vn: 'Quyền' }[l],
        changed: {
          ru: 'изменено: {count}',
          en: 'changed: {count}',
          vn: 'đã thay đổi: {count}',
        }[l],
        changedFooter: {
          ru: 'Изменено ячеек: {count}',
          en: 'Changed cells: {count}',
          vn: 'Ô đã thay đổi: {count}',
        }[l],
        locks: {
          system: {
            ru: 'Права системной роли правятся только миграцией',
            en: 'Permissions of a system role are changed only by a migration',
            vn: 'Quyền của vai trò hệ thống chỉ thay đổi bằng migration',
          }[l],
          ownSection: {
            ru: 'Нельзя снять право, которым открывается ваш раздел',
            en: 'You cannot remove a permission that opens your own section',
            vn: 'Không thể gỡ quyền đang mở mục của chính bạn',
          }[l],
        },
        legend: {
          active: { ru: '✔ активно', en: '✔ active', vn: '✔ đang bật' }[l],
          system: {
            ru: '⛊ системная роль — только миграцией',
            en: '⛊ system role — migration only',
            vn: '⛊ vai trò hệ thống — chỉ migration',
          }[l],
          ownSection: {
            ru: '⨯ гейтит ваш раздел — снять нельзя',
            en: '⨯ gates your section — cannot be removed',
            vn: '⨯ mở mục của bạn — không thể gỡ',
          }[l],
          changed: {
            ru: '▣ изменено, не сохранено',
            en: '▣ changed, not saved',
            vn: '▣ đã đổi, chưa lưu',
          }[l],
        },
        toast: {
          saved: { ru: 'Матрица сохранена', en: 'Matrix saved', vn: 'Đã lưu ma trận' }[l],
        },
      },
      sections: {
        title: { ru: 'Реестр разделов', en: 'Section registry', vn: 'Danh mục mục' }[l],
        readOnly: { ru: 'только чтение', en: 'read-only', vn: 'chỉ đọc' }[l],
        hint: {
          ru: 'Реестр меняется миграцией; здесь он показан для сверки. Раздел без прав доступен всем, кого пускает приложение.',
          en: 'The registry is changed by a migration; it is shown here for reference. A section without permissions is open to anyone allowed into the application.',
          vn: 'Danh mục được thay đổi bằng migration; ở đây chỉ để đối chiếu. Mục không có quyền sẽ mở cho mọi người vào được ứng dụng.',
        }[l],
        columns: {
          key: { ru: 'Ключ', en: 'Key', vn: 'Khóa' }[l],
          app: { ru: 'Приложение', en: 'Application', vn: 'Ứng dụng' }[l],
          order: { ru: 'Порядок', en: 'Order', vn: 'Thứ tự' }[l],
          permissions: { ru: 'Требуемые права', en: 'Required permissions', vn: 'Quyền yêu cầu' }[
            l
          ],
          public: { ru: 'Публичный', en: 'Public', vn: 'Công khai' }[l],
        },
        public: { ru: 'публичный', en: 'public', vn: 'công khai' }[l],
        none: { ru: '— не требуется', en: '— not required', vn: '— không yêu cầu' }[l],
        unknownKey: {
          ru: 'Ключа нет во фронтовом реестре: пункт меню не появится, пока его не добавят в SECTION_KEYS',
          en: 'The key is missing from the frontend registry: the menu item will not appear until it is added to SECTION_KEYS',
          vn: 'Khóa không có trong danh mục frontend: mục menu sẽ không xuất hiện cho tới khi thêm vào SECTION_KEYS',
        }[l],
        empty: {
          title: { ru: 'Реестр пуст', en: 'Registry is empty', vn: 'Danh mục trống' }[l],
          description: {
            ru: 'Разделы заводятся миграцией — вероятно, посев ещё не доехал',
            en: 'Sections are seeded by a migration — the seed has probably not arrived yet',
            vn: 'Các mục được tạo bằng migration — có lẽ chưa được áp dụng',
          }[l],
        },
      },
      audit: {
        title: {
          ru: 'Журнал изменений доступов',
          en: 'Access change log',
          vn: 'Nhật ký thay đổi truy cập',
        }[l],
        hint: {
          ru: 'Развёрнутая строка показывает, какие права добавлены и какие сняты.',
          en: 'An expanded row shows which permissions were added and which were removed.',
          vn: 'Dòng mở rộng cho biết quyền nào được thêm và quyền nào bị gỡ.',
        }[l],
        filters: {
          actor: { ru: 'Администратор', en: 'Administrator', vn: 'Quản trị viên' }[l],
          action: { ru: 'Действие', en: 'Action', vn: 'Hành động' }[l],
          targetType: { ru: 'Тип объекта', en: 'Object type', vn: 'Loại đối tượng' }[l],
          from: { ru: 'С даты', en: 'From', vn: 'Từ ngày' }[l],
          to: { ru: 'По дату', en: 'To', vn: 'Đến ngày' }[l],
          any: { ru: 'Любой', en: 'Any', vn: 'Bất kỳ' }[l],
          reset: { ru: 'Сбросить', en: 'Reset', vn: 'Đặt lại' }[l],
        },
        columns: {
          time: { ru: 'Время', en: 'Time', vn: 'Thời gian' }[l],
          actor: { ru: 'Администратор', en: 'Administrator', vn: 'Quản trị viên' }[l],
          action: { ru: 'Действие', en: 'Action', vn: 'Hành động' }[l],
          target: { ru: 'Объект', en: 'Object', vn: 'Đối tượng' }[l],
          details: { ru: 'Детали', en: 'Details', vn: 'Chi tiết' }[l],
        },
        // Коды — значения CHECK-ограничения rbac_audit_logs (миграция 52).
        actions: {
          ROLE_CREATE: { ru: 'роль создана', en: 'role created', vn: 'tạo vai trò' }[l],
          ROLE_UPDATE: { ru: 'роль изменена', en: 'role updated', vn: 'sửa vai trò' }[l],
          ROLE_DELETE: { ru: 'роль удалена', en: 'role deleted', vn: 'xóa vai trò' }[l],
          PERMISSION_CREATE: { ru: 'право заведено', en: 'permission created', vn: 'tạo quyền' }[l],
          PERMISSION_UPDATE: { ru: 'право изменено', en: 'permission updated', vn: 'sửa quyền' }[l],
          PERMISSION_DELETE: { ru: 'право удалено', en: 'permission deleted', vn: 'xóa quyền' }[l],
          ROLE_ASSIGN: { ru: 'роль назначена', en: 'role assigned', vn: 'gán vai trò' }[l],
          ROLE_REVOKE: { ru: 'роль снята', en: 'role removed', vn: 'gỡ vai trò' }[l],
          SECTION_UPDATE: { ru: 'раздел изменён', en: 'section updated', vn: 'sửa mục' }[l],
          INVITATION_CREATE: {
            ru: 'приглашение отправлено',
            en: 'invitation sent',
            vn: 'gửi lời mời',
          }[l],
          INVITATION_REVOKE: {
            ru: 'приглашение отозвано',
            en: 'invitation revoked',
            vn: 'thu hồi lời mời',
          }[l],
        },
        targets: {
          ROLE: { ru: 'роль', en: 'role', vn: 'vai trò' }[l],
          PERMISSION: { ru: 'право', en: 'permission', vn: 'quyền' }[l],
          USER: { ru: 'пользователь', en: 'user', vn: 'người dùng' }[l],
          SECTION: { ru: 'раздел', en: 'section', vn: 'mục' }[l],
          INVITATION: { ru: 'приглашение', en: 'invitation', vn: 'lời mời' }[l],
        },
        details: {
          added: { ru: 'добавлено', en: 'added', vn: 'đã thêm' }[l],
          removed: { ru: 'снято', en: 'removed', vn: 'đã gỡ' }[l],
          roles: { ru: 'роли', en: 'roles', vn: 'vai trò' }[l],
          user: { ru: 'пользователь', en: 'user', vn: 'người dùng' }[l],
          expand: { ru: 'развернуть', en: 'expand', vn: 'mở rộng' }[l],
          collapse: { ru: 'свернуть', en: 'collapse', vn: 'thu gọn' }[l],
          none: { ru: 'без деталей', en: 'no details', vn: 'không có chi tiết' }[l],
        },
        deletedActor: { ru: 'учётка удалена', en: 'account deleted', vn: 'tài khoản đã xóa' }[l],
        empty: {
          title: { ru: 'Записей нет', en: 'No entries', vn: 'Không có bản ghi' }[l],
          description: {
            ru: 'За выбранный период изменений доступов не было',
            en: 'No access changes in the selected period',
            vn: 'Không có thay đổi truy cập trong khoảng đã chọn',
          }[l],
        },
      },
    },
  },
})

export const adminRbacMessages = {
  ru: build('ru'),
  en: build('en'),
  vn: build('vn'),
}
